import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, Download } from "lucide-react";
import portfolio from "@/data/portfolio";

const links = [
  { to: "/#about", label: "About" },
  { to: "/#career", label: "Career" },
  { to: "/projects/national-equestrian-league", label: "NEL" },
  { to: "/projects/madrid-horse-week", label: "Madrid Horse Week" },
  { to: "/projects/ridetv", label: "Ryde TV" },
  { to: "/#expertise", label: "Expertise" },
  { to: "/#contact", label: "Contact" },
];

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    window.addEventListener("scroll", onScroll);
    onScroll();
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <motion.header
      initial={{ y: -24, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
      data-testid="site-navbar"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        scrolled
          ? "glass border-b hairline"
          : "bg-transparent border-b border-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12 h-16 md:h-20 flex items-center justify-between">
        <a
          href="#top"
          data-testid="navbar-logo"
          className="font-serif-display text-xl md:text-2xl tracking-tight text-black"
        >
          Pablo<span className="text-gray-400"> · </span>Marquez
        </a>

        <nav className="hidden lg:flex items-center gap-8">
          {links.map((l) => (
            <Link
              key={l.to}
              to={l.to}
              data-testid={`nav-link-${l.to.replace(/[^a-z0-9]+/gi, "-").replace(/^-|-$/g, "")}`}
              className="text-sm text-gray-600 hover:text-black transition-colors"
            >
              {l.label}
            </Link>
          ))}
        </nav>

        <div className="flex items-center gap-3">
          <a
            href={`${BACKEND_URL}/api/cv/download`}
            data-testid="navbar-download-cv"
            className="hidden md:inline-flex items-center gap-2 bg-black text-white hover:bg-[#222] rounded-full px-5 py-2.5 text-sm font-medium transition-colors"
          >
            <Download className="w-4 h-4" /> CV
          </a>
          <button
            onClick={() => setOpen(!open)}
            data-testid="navbar-menu-toggle"
            className="lg:hidden p-2 rounded-full hover:bg-gray-100 transition-colors"
            aria-label="Toggle menu"
          >
            {open ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </button>
        </div>
      </div>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="lg:hidden overflow-hidden glass border-t hairline"
            data-testid="mobile-menu"
          >
            <nav className="flex flex-col px-6 py-6 gap-4">
              {links.map((l) => (
                <Link
                  key={l.to}
                  to={l.to}
                  onClick={() => setOpen(false)}
                  data-testid={`mobile-link-${l.to.replace(/[^a-z0-9]+/gi, "-").replace(/^-|-$/g, "")}`}
                  className="text-base text-gray-700 hover:text-black"
                >
                  {l.label}
                </Link>
              ))}
              <a
                href={`${BACKEND_URL}/api/cv/download`}
                data-testid="mobile-download-cv"
                className="mt-2 inline-flex items-center gap-2 bg-black text-white rounded-full px-5 py-3 text-sm font-medium w-fit"
              >
                <Download className="w-4 h-4" /> Download CV
              </a>
            </nav>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.header>
  );
}
